/**
 * NewPasswordFields — the New Password + Confirm pair with the live password-policy hint.
 *
 * Purpose: one place for choosing a password (ORCH-396 policy), shared by the
 *          Accept Invite and Reset Password pages (ORCH-296) so the rule text and the
 *          check can't drift between them. The page owns the values and the submit.
 */

import React from 'react';
import { TextField } from '@mui/material';
import { validatePassword } from '../../utils/validation';

export const PASSWORD_RULE =
  'At least 8 characters, with an uppercase letter, a lowercase letter, a number and a special character';

export interface NewPasswordFieldsProps {
  /** Prefix for the input ids: `<idPrefix>-password`, `<idPrefix>-password-confirm` */
  idPrefix: string;
  password: string;
  confirmPassword: string;
  onPasswordChange: (value: string) => void;
  onConfirmPasswordChange: (value: string) => void;
  disabled?: boolean;
  passwordLabel?: string;
}

/**
 * The password problems a submit should report, or null when the pair is acceptable.
 */
export const newPasswordError = (password: string, confirmPassword: string): string | null => {
  const check = validatePassword(password);
  if (!check.valid) return check.errors.join('. ') + '.';
  if (password !== confirmPassword) return 'Passwords do not match.';
  return null;
};

const NewPasswordFields: React.FC<NewPasswordFieldsProps> = ({
  idPrefix,
  password,
  confirmPassword,
  onPasswordChange,
  onConfirmPasswordChange,
  disabled = false,
  passwordLabel = 'Password',
}) => {
  // The rule is shown from the start, and what is still missing once they type (ORCH-396)
  const passwordIssues = password ? validatePassword(password).errors : [];

  return (
    <>
      <TextField
        id={`${idPrefix}-password`}
        label={passwordLabel}
        type="password"
        value={password}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => onPasswordChange(e.target.value)}
        autoComplete="new-password"
        required
        fullWidth
        disabled={disabled}
        error={passwordIssues.length > 0}
        helperText={passwordIssues.length > 0 ? passwordIssues.join('. ') + '.' : PASSWORD_RULE}
        FormHelperTextProps={{ id: `${idPrefix}-password-rule` }}
      />
      <TextField
        id={`${idPrefix}-password-confirm`}
        label="Confirm password"
        type="password"
        value={confirmPassword}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          onConfirmPasswordChange(e.target.value)
        }
        autoComplete="new-password"
        required
        fullWidth
        disabled={disabled}
      />
    </>
  );
};

export default NewPasswordFields;
